import { useState } from 'react';
import type { FilterValue, SorterResult, TablePaginationConfig } from 'antd/es/table/interface';

export type ServerSortOrder = 'asc' | 'desc';

export interface ServerSortState {
  sortBy?: string;
  sortOrder?: ServerSortOrder;
}

export function parseServerSorter<T extends object>(
  sorter: SorterResult<T> | SorterResult<T>[],
): ServerSortState {
  const current = Array.isArray(sorter) ? sorter[0] : sorter;
  const field = current?.columnKey ?? current?.field;
  if (typeof field !== 'string' || current?.order == null) return {};
  return {
    sortBy: field,
    sortOrder: current.order === 'ascend' ? 'asc' : 'desc',
  };
}

export function serverSortProps(
  field: string,
  state: ServerSortState,
): { sorter: true; sortOrder?: 'ascend' | 'descend' } {
  return {
    sorter: true,
    sortOrder:
      state.sortBy === field ? (state.sortOrder === 'asc' ? 'ascend' : 'descend') : undefined,
  };
}

export function useServerPaginacion<T extends object = Record<string, unknown>>(
  initialPageSize = 10,
  initialSort: ServerSortState = {},
) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [sort, setSort] = useState<ServerSortState>(initialSort);

  const handleChange = (
    pagination: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    sorter: SorterResult<T> | SorterResult<T>[],
  ) => {
    const nextSort = parseServerSorter(sorter);
    const sortChanged = nextSort.sortBy !== sort.sortBy || nextSort.sortOrder !== sort.sortOrder;
    if (sortChanged) {
      setSort(nextSort);
      setPage(1);
      return;
    }

    const nextPageSize = pagination.pageSize ?? pageSize;
    if (nextPageSize !== pageSize) {
      setPageSize(nextPageSize);
      setPage(1);
    } else {
      setPage(pagination.current ?? 1);
    }
  };

  return { page, pageSize, sort, handleChange, setPage };
}
